import { generateUUID } from "src/services";

import type {
  CharacterStyleAction,
  CharStyleFormattingOrigin,
  ParagraphFormattingInfo,
} from "../character_styles/types";
import type { CharacterStyleProfileEntry } from "src/types";

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────
export const TAG_PREFIX = "mss:c:";
export const NO_CHARACTER_STYLE = "__no_character_style__";
export const NO_CHARACTER_STYLE_DISPLAY = "No Character Style";

export type CharStyleChangeSource = "initial" | "applicator" | "accept" | "reject" | "restyle";

export interface CharStyleFormattingFlags {
  italic: boolean;
  bold: boolean;
  underline: boolean;
  strikethrough: boolean;
}

export interface CharStyleState {
  formatting: CharStyleFormattingFlags;
  characterStyle: string | null;
}

export interface CharStyleChangeEntry {
  previousState: CharStyleState;
  newState: CharStyleState;
  source: CharStyleChangeSource;
  timestamp: string;
}

export interface CharacterStyleRecord {
  id: string;
  paragraphTag: string;
  paragraphUniqueLocalId: string;
  actionType: "remove_formatting" | "apply_style" | "new_style"; // (SJ) will we even need "remove_formatting".. leaving in for QA purposes for now
  actionDetail: string;
  actionTarget: "paragraph" | "entity" | "span";
  spanIndex: number | null;
  textFingerprint: string;
  rangeStart: number;
  rangeEnd: number;
  originalState: CharStyleState;
  appliedState: CharStyleState;
  isPending: boolean;
  trackedChangeCount: number;
  history: CharStyleChangeEntry[];
  /** Which rule produced this action (e.g., "I-1", "DIA-1", "B"). */
  ruleId: string;
  /** The paragraph style code at the time character styles were applied. */
  paragraphStyle: string;
  /** Entity hint type for entity-targeted actions (e.g., "speaker", "url"). Null for non-entity actions. */
  entityType: string | null;
  formattingOrigin: CharStyleFormattingOrigin;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function getTimestamp(): string {
  return new Date().toISOString();
}

// Keeps only characters valid in the XML 1.0 `Char` production. Word's
// setXml() re-parses the serialized string and rejects illegal characters
// (C0 controls such as a Shift+Enter line break 0x0B, and lone surrogates)
// with InvalidArgument. Iterating by code point preserves valid surrogate
// pairs while dropping unpaired ones.
function sanitizeXmlText(s: string): string {
  let out = "";
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    if (
      cp === 0x9 ||
      cp === 0xa ||
      cp === 0xd ||
      (cp >= 0x20 && cp <= 0xd7ff) ||
      (cp >= 0xe000 && cp <= 0xfffd) ||
      (cp >= 0x10000 && cp <= 0x10ffff)
    ) {
      out += ch;
    }
  }
  return out;
}

function buildFingerprint(text: string): string {
  return sanitizeXmlText(text.slice(0, 40));
}

function stateFromSpan(span: {
  italic: boolean;
  bold: boolean;
  underline: boolean;
  strikethrough: boolean;
  characterStyle: string | null;
}): CharStyleState {
  return {
    formatting: {
      italic: span.italic,
      bold: span.bold,
      underline: span.underline,
      strikethrough: span.strikethrough,
    },
    characterStyle: span.characterStyle,
  };
}

function formattingFlagsFromSourceStyle(sourceStyle: string): CharStyleFormattingFlags {
  const parts = sourceStyle.split(",");
  return {
    italic: parts.includes("italic"),
    bold: parts.includes("bold"),
    underline: parts.includes("underline"),
    strikethrough: parts.includes("strikethrough"),
  };
}

function computeAppliedState(
  original: CharStyleState,
  action: CharacterStyleAction,
  characterStyles: CharacterStyleProfileEntry[]
): CharStyleState {
  if (action.type === "remove_formatting") {
    const newFormatting = { ...original.formatting };
    switch (action.formatting) {
      case "italic":
        newFormatting.italic = false;
        break;
      case "bold":
        newFormatting.bold = false;
        break;
      case "underline":
        newFormatting.underline = false;
        break;
      case "strikethrough":
        newFormatting.strikethrough = false;
        break;
    }
    return { formatting: newFormatting, characterStyle: original.characterStyle };
  }

  const profileEntry = characterStyles.find((e) => e.style === action.style);
  const newFormatting = { ...original.formatting };
  if (profileEntry?.source_style) {
    const implied = formattingFlagsFromSourceStyle(profileEntry.source_style);
    if (implied.italic) newFormatting.italic = true;
    if (implied.bold) newFormatting.bold = true;
    if (implied.underline) newFormatting.underline = true;
    if (implied.strikethrough) newFormatting.strikethrough = true;
  }
  return { formatting: newFormatting, characterStyle: action.style };
}

function actionDetailFromAction(action: CharacterStyleAction): string {
  if (action.type === "remove_formatting") {
    return action.formatting;
  }
  return action.style;
}

// ────────────────────────────────────────────────────────────────────────────
// Public Functions
// ────────────────────────────────────────────────────────────────────────────

/**
 * Creates character style records from the actions that were applied to a paragraph.
 * Called after the applicator executes actions, once per paragraph.
 */
export function createCharStyleRecords(
  paragraphTag: string,
  paragraphUniqueLocalId: string,
  actions: CharacterStyleAction[],
  formattingInfo: ParagraphFormattingInfo,
  paragraphStyle?: string,
  characterStyles?: CharacterStyleProfileEntry[]
): CharacterStyleRecord[] {
  const records: CharacterStyleRecord[] = [];
  // Accumulate removal actions by shared key so multiple removals on the same
  // span (e.g. bold + underline) produce one merged record with combined actionDetail.
  const removalRecordsByKey = new Map<string, CharacterStyleRecord>();

  for (const action of actions) {
    if (!action.tracked) {
      continue;
    }
    let spanIndex: number | null = null;
    let textFingerprint = "";
    let rangeStart = 0;
    let rangeEnd = 0;
    let originalState: CharStyleState;

    if (action.type === "apply_style" && action.target === "span" && action.spanIndex != null) {
      spanIndex = action.spanIndex;
      const span = formattingInfo.spans[spanIndex];
      if (!span) continue;
      textFingerprint = buildFingerprint(span.text);
      rangeStart = span.start;
      rangeEnd = span.end;
      originalState = stateFromSpan(span);
    } else if (action.type === "apply_style" && action.target === "entity" && action.targetText) {
      textFingerprint = buildFingerprint(action.targetText);
      const idx = action.targetStartIndex ?? formattingInfo.text.indexOf(action.targetText);
      rangeStart = idx >= 0 ? idx : 0;
      rangeEnd = rangeStart + action.targetText.length;
      const coveringSpan = formattingInfo.spans.find(
        (s) => s.start <= rangeStart && s.end >= rangeEnd
      );
      originalState = coveringSpan
        ? stateFromSpan(coveringSpan)
        : {
            formatting: { italic: false, bold: false, underline: false, strikethrough: false },
            characterStyle: null,
          };
    } else if (action.type === "apply_style" && action.target === "paragraph") {
      textFingerprint = buildFingerprint(formattingInfo.text);
      rangeStart = 0;
      rangeEnd = formattingInfo.text.length;
      const firstSpan = formattingInfo.spans[0];
      originalState = firstSpan
        ? stateFromSpan(firstSpan)
        : {
            formatting: { italic: false, bold: false, underline: false, strikethrough: false },
            characterStyle: null,
          };
    } else if (action.type === "remove_formatting" && action.target === "span" && action.spanIndex != null) {
      spanIndex = action.spanIndex;
      const span = formattingInfo.spans[spanIndex];
      if (!span) continue;
      textFingerprint = buildFingerprint(span.text);
      rangeStart = span.start;
      rangeEnd = span.end;
      originalState = stateFromSpan(span);
    } else if (action.type === "remove_formatting") {
      textFingerprint = buildFingerprint(formattingInfo.text);
      rangeStart = 0;
      rangeEnd = formattingInfo.text.length;
      const firstSpan = formattingInfo.spans[0];
      originalState = firstSpan
        ? stateFromSpan(firstSpan)
        : {
            formatting: { italic: false, bold: false, underline: false, strikethrough: false },
            characterStyle: null,
          };
    } else {
      continue;
    }

    // Merge removal actions targeting the same span into one record.
    if (action.type === "remove_formatting") {
      const key = action.target === "span" ? `span:${action.spanIndex}` : "paragraph";
      const existing = removalRecordsByKey.get(key);
      if (existing) {
        existing.actionDetail += `,${action.formatting}`;
        existing.appliedState = computeAppliedState(
          existing.appliedState,
          action,
          characterStyles ?? []
        );
        continue;
      }
    }

    const appliedState = computeAppliedState(originalState, action, characterStyles ?? []);

    const record: CharacterStyleRecord = {
      id: action.actionId ?? TAG_PREFIX + generateUUID(),
      paragraphTag,
      paragraphUniqueLocalId,
      actionType: action.type,
      actionDetail: actionDetailFromAction(action),
      actionTarget: action.type === "remove_formatting" ? (action.target ?? "paragraph") : action.target,
      spanIndex,
      textFingerprint,
      rangeStart,
      rangeEnd,
      originalState,
      appliedState,
      isPending: true,
      trackedChangeCount: 1,
      history: [
        {
          previousState: originalState,
          newState: appliedState,
          source: "applicator",
          timestamp: getTimestamp(),
        },
      ],
      ruleId: action.ruleId ?? "",
      paragraphStyle: paragraphStyle ?? "",
      entityType: (action.type === "apply_style" ? action.entityType : undefined) ?? null,
      formattingOrigin: action.formattingOrigin,
    };

    if (action.type === "remove_formatting") {
      const key = action.target === "span" ? `span:${action.spanIndex}` : "paragraph";
      removalRecordsByKey.set(key, record);
    }

    records.push(record);
  }

  return records;
}

/**
 * Creates a new StyleChangeEntry.
 */
function createStyleChangeEntry(
  previousState: CharStyleState,
  newState: CharStyleState,
  source: CharStyleChangeSource
): CharStyleChangeEntry {
  return {
    previousState,
    newState,
    source,
    timestamp: getTimestamp(),
  };
}

/**
 * Marks paragraphs as restyled (manual style change).
 *
 * Updates the style to the new user-selected style.
 * trackedChangeCount is incremented.
 * isPending is set to false (restyle auto-approves).
 * A 'restyle' entry is added to styleHistory.
 *
 * @param records - Current style records
 * @param ids - Array of uniqueLocalId values to mark as restyled
 * @param newStyle - The new style applied by the user
 * @returns New array of updated ParagraphStyleRecord
 */
export function markCharAsRestyled(
  records: CharacterStyleRecord[],
  ids: string[],
  newStyle: string
): CharacterStyleRecord[] {
  const idSet = new Set(ids);
  const newFormatting = formattingFlagsFromSourceStyle(newStyle);

  return records.map<CharacterStyleRecord>((record) => {
    if (!idSet.has(record.id)) {
      return record;
    }

    // Skip if style is the same (no actual change)
    if (record.appliedState.characterStyle === newStyle) {
      return {
        ...record,
        actionType: record.actionType === "remove_formatting" ? "apply_style" : record.actionType,
        actionDetail: record.actionType === "remove_formatting" ? newStyle : record.actionDetail,
        isPending: false,
      };
    }

    return {
      ...record,
      actionType: record.actionType === "remove_formatting" ? "apply_style" : record.actionType,
      actionDetail: record.actionType === "remove_formatting" ? newStyle : record.actionDetail,
      formattingOrigin: "restyled",
      appliedState: {
        characterStyle: newStyle,
        formatting: newFormatting,
      },
      isPending: false,
      history: [
        ...record.history,
        createStyleChangeEntry(
          record.originalState,
          { characterStyle: newStyle, formatting: newFormatting },
          "restyle"
        ),
      ],
    };
  });
}

/**
 * Marks character style records as accepted.
 * Sets isPending=false, adds 'accept' entry to history.
 * trackedChangeCount is NOT incremented (acceptance is not a change).
 */
export function markCharStylesAccepted(
  records: CharacterStyleRecord[],
  ids: string[]
): CharacterStyleRecord[] {
  const idSet = new Set(ids);

  return records.map((record) => {
    if (!idSet.has(record.id) || !record.isPending) {
      return record;
    }

    return {
      ...record,
      isPending: false,
      history: [
        ...record.history,
        {
          previousState: record.appliedState,
          newState: record.appliedState,
          source: "accept" as const,
          timestamp: getTimestamp(),
        },
      ],
    };
  });
}

/**
 * Marks character style records as rejected.
 * Reverts appliedState to originalState, increments trackedChangeCount,
 * sets isPending=false, adds 'reject' entry to history.
 */
export function markCharStylesRejected(
  records: CharacterStyleRecord[],
  ids: string[]
): CharacterStyleRecord[] {
  return records.filter((record) => !ids.includes(record.id));
}

/**
 * Re-maps stale paragraphUniqueLocalId values using the persistent
 * paragraphTag as the stable join key. Called on session resume.
 */
export function remapStaleCharStyleRecords(
  records: CharacterStyleRecord[],
  tagToLocalId: Map<string, string>
): CharacterStyleRecord[] {
  return records.map((record) => {
    if (!record.paragraphTag) return record;
    const currentLocalId = tagToLocalId.get(record.paragraphTag);
    if (!currentLocalId || currentLocalId === record.paragraphUniqueLocalId) return record;
    return { ...record, paragraphUniqueLocalId: currentLocalId };
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Query Helpers
// ────────────────────────────────────────────────────────────────────────────

export function getCharStyleRecordById(
  records: CharacterStyleRecord[],
  id: string
): CharacterStyleRecord | undefined {
  return records.find((r) => r.id === id);
}

export function getPendingCharStyleCount(records: CharacterStyleRecord[]): number {
  return records.filter((r) => r.isPending).length;
}

export function getPendingCharStyleRecords(
  records: CharacterStyleRecord[]
): CharacterStyleRecord[] {
  return records.filter((r) => r.isPending);
}

export function getCharStyleRecordsForParagraph(
  records: CharacterStyleRecord[],
  paragraphTag: string
): CharacterStyleRecord[] {
  return records.filter((r) => r.paragraphTag === paragraphTag);
}

export function getPendingCharStyleRecordsForParagraph(
  records: CharacterStyleRecord[],
  paragraphTag: string
): CharacterStyleRecord[] {
  return records.filter((r) => r.paragraphTag === paragraphTag && r.isPending);
}
